/*
 * Valoured — Prelaunch Hero (custom)
 * Two native Web Components, following Dawn's conventions:
 *   <v-prelaunch-hero> — pointer-driven parallax for [data-depth] children
 *   <v-signup-form>    — custom-designed form that posts to Klaviyo's
 *                        client-side subscribe API (phone by default, email fallback)
 */

const V_REDUCED_MOTION =
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!customElements.get('v-prelaunch-hero')) {
  class VPrelaunchHero extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      // Takeover: this section covers Dawn's header/footer, so lock the
      // underlying page scroll to avoid a phantom scrollbar. Restored on disconnect.
      this.prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      this.enabled = this.dataset.parallax === 'true' && !V_REDUCED_MOTION;
      this.layers = Array.from(this.querySelectorAll('[data-depth]'));
      this.target = { x: 0, y: 0 };
      this.current = { x: 0, y: 0 };
      this.rafId = null;

      if (!this.enabled || this.layers.length === 0) return;

      this.onPointerMove = this.onPointerMove.bind(this);
      this.tick = this.tick.bind(this);
      window.addEventListener('pointermove', this.onPointerMove, { passive: true });
      this.tick();
    }

    disconnectedCallback() {
      window.removeEventListener('pointermove', this.onPointerMove);
      if (this.rafId) cancelAnimationFrame(this.rafId);
      document.body.style.overflow = this.prevOverflow || '';
    }

    onPointerMove(event) {
      // Normalize pointer position to -1..1 around the viewport center.
      this.target.x = (event.clientX / window.innerWidth) * 2 - 1;
      this.target.y = (event.clientY / window.innerHeight) * 2 - 1;
    }

    tick() {
      // Ease toward the target for a smooth, weighted feel.
      this.current.x += (this.target.x - this.current.x) * 0.08;
      this.current.y += (this.target.y - this.current.y) * 0.08;

      const range = parseFloat(this.style.getPropertyValue('--v-parallax-range')) || 40;

      for (const el of this.layers) {
        const depth = parseFloat(el.dataset.depth) || 0;
        const x = -this.current.x * range * depth;
        const y = -this.current.y * range * depth;
        const base = el.classList.contains('v-prelaunch__layer') ? ' scale(1.12)' : '';
        el.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)${base}`;
      }

      this.rafId = requestAnimationFrame(this.tick);
    }
  }

  customElements.define('v-prelaunch-hero', VPrelaunchHero);
}

if (!customElements.get('v-signup-form')) {
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  const KLAVIYO_REVISION = '2026-04-15';

  class VSignupForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('[novalidate]');
      this.input = this.querySelector('[data-signup-field]');
      this.label = this.querySelector('[data-signup-label]');
      this.submit = this.querySelector('[data-signup-submit]');
      this.toggle = this.querySelector('[data-signup-toggle]');
      this.message = this.querySelector('[data-signup-message]');
      this.spinner = this.querySelector('.v-signup__spinner');
      this.idleIcon = this.querySelector('.v-signup__submit-idle');

      this.mode = 'phone'; // 'phone' | 'email'
      this.busy = false;

      if (!this.form || !this.input) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.toggle?.addEventListener('click', this.onToggle.bind(this));
    }

    get config() {
      const d = this.dataset;
      return {
        publicKey: (d.klaviyoKey || '').trim(),
        phoneListId: (d.phoneListId || '').trim(),
        emailListId: (d.emailListId || '').trim(),
        countryCode: (d.countryCode || '+1').trim(),
        smsConsent: d.smsConsent === 'true',
        source: d.source || 'Prelaunch Landing',
        placeholders: {
          phone: d.phonePlaceholder || 'ENTER YOUR PHONE NUMBER',
          email: d.emailPlaceholder || 'ENTER YOUR EMAIL',
        },
        toggles: {
          // Label shown on the toggle points to the OTHER mode.
          phone: d.phoneToggle || 'Use phone instead',
          email: d.emailToggle || 'Use email instead',
        },
        messages: {
          success: d.msgSuccess || "You're on the list.",
          error: d.msgError || 'Something went wrong. Please try again.',
          invalidPhone: d.msgInvalidPhone || 'Enter a valid phone number.',
          invalidEmail: d.msgInvalidEmail || 'Enter a valid email address.',
        },
      };
    }

    onToggle() {
      const cfg = this.config;
      this.mode = this.mode === 'phone' ? 'email' : 'phone';

      if (this.mode === 'email') {
        this.input.type = 'email';
        this.input.inputMode = 'email';
        this.input.autocomplete = 'email';
        this.input.placeholder = cfg.placeholders.email;
        this.toggle.textContent = cfg.toggles.phone;
      } else {
        this.input.type = 'tel';
        this.input.inputMode = 'tel';
        this.input.autocomplete = 'tel';
        this.input.placeholder = cfg.placeholders.phone;
        this.toggle.textContent = cfg.toggles.email;
      }

      if (this.label) this.label.textContent = this.input.placeholder;
      this.clearMessage();
      this.input.value = '';
      this.input.focus();
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;

      const cfg = this.config;
      const raw = this.input.value.trim();

      const profile = {};
      let listId;
      if (this.mode === 'email') {
        if (!EMAIL_RE.test(raw)) return this.showError(cfg.messages.invalidEmail);
        profile.email = raw;
        listId = cfg.emailListId;
      } else {
        const phone = this.normalizePhone(raw, cfg.countryCode);
        if (!phone) return this.showError(cfg.messages.invalidPhone);
        profile.phone_number = phone;
        listId = cfg.phoneListId;
      }

      if (!cfg.publicKey || !listId) {
        // Misconfiguration: surface it clearly rather than silently failing.
        return this.showError('Signup is not configured yet.');
      }

      this.setBusy(true);
      try {
        const ok = await this.subscribe(profile, cfg, listId);
        if (ok) {
          this.showSuccess(cfg.messages.success);
          this.classList.add('v-signup--done');
        } else {
          this.showError(cfg.messages.error);
          this.setBusy(false);
        }
      } catch (err) {
        this.showError(cfg.messages.error);
        this.setBusy(false);
      }
    }

    async subscribe(profile, cfg, listId) {
      const subscriptions = {};
      if (profile.email) {
        subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
      }
      if (profile.phone_number && cfg.smsConsent) {
        subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
      }

      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: cfg.source,
            profile: {
              data: {
                type: 'profile',
                attributes: { ...profile, subscriptions },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: listId } },
          },
        },
      };

      const url = `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(cfg.publicKey)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
      });

      // Klaviyo returns 202 Accepted on success.
      return res.status === 202 || res.ok;
    }

    normalizePhone(value, countryCode) {
      const hasPlus = value.trim().startsWith('+');
      const digits = value.replace(/[^\d]/g, '');
      if (digits.length < 7) return null;
      if (hasPlus) return `+${digits}`;
      const cc = countryCode.replace(/[^\d]/g, '');
      return `+${cc}${digits}`;
    }

    setBusy(busy) {
      this.busy = busy;
      if (this.submit) this.submit.disabled = busy;
      if (this.spinner) this.spinner.hidden = !busy;
      if (this.idleIcon) this.idleIcon.hidden = busy;
    }

    showError(text) {
      this.setMessage(text, 'error');
    }

    showSuccess(text) {
      this.setMessage(text, 'success');
    }

    setMessage(text, kind) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.hidden = false;
      this.message.classList.remove('v-signup__message--error', 'v-signup__message--success');
      this.message.classList.add(`v-signup__message--${kind}`);
    }

    clearMessage() {
      if (!this.message) return;
      this.message.hidden = true;
      this.message.textContent = '';
    }
  }

  customElements.define('v-signup-form', VSignupForm);
}
